import MultiSelectInput from "@/components/forms/inputs/MultiSelectInput";
import SelectInput from "@/components/forms/inputs/SelectInput";
import { SelectValue } from "@/components/ui/select";
import { DynamicSupplyHardwareFiltersResponse } from "@/types";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import React, { useMemo, useState } from "react";
import { RangeFilter } from "./RangeFilter";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";

const REGIONS_MAP: Record<string, string> = {
  EMEA: "Europe, Middle East & Africa",
  APAC: "Asia Pacific",
  "LATAM America": "Latin America",
  "Northern America": "North America"
};

export type FilterKey =
  | "gpuFamily"
  | "gpuMemory"
  | "regions"
  | "cpuCores"
  | "deviceMemory"
  | "storage"
  | "supplier"
  | "confidentialCompute";

export type FilterValues = Record<FilterKey, string[]>;

export type FilterConfig = Record<
  FilterKey,
  { label: string; options: { label: string; value: string }[]; disabled?: boolean }
>;

const formatMbSmart = (mb: number) => {
  if (typeof mb !== "number" || isNaN(mb)) return "-";
  if (mb < 1024) {
    return `${mb} MB`;
  }
  const gb = mb / 1024;
  const value = gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1);
  return `${parseFloat(value)} GB`;
};

const buildSteppedRangeMB = (min: number, max: number) => {
  if (typeof min !== "number" || typeof max !== "number" || min >= max) {
    return [] as string[];
  }
  const steps = [0, 0.25, 0.5, 0.75, 1];
  const unique: number[] = [];
  steps.forEach((s) => {
    const v = Math.round(min + (max - min) * s);
    if (!unique.includes(v)) unique.push(v);
  });
  return unique.map((v) => `${formatMbSmart(v)}`);
};

const buildSteppedRangeInt = (min: number, max: number) => {
  if (typeof min !== "number" || typeof max !== "number" || min >= max) {
    return [] as string[];
  }
  const steps = [0, 0.25, 0.5, 0.75, 1];
  const unique: number[] = [];
  steps.forEach((s) => {
    const v = Math.round(min + (max - min) * s);
    if (!unique.includes(v)) unique.push(v);
  });
  return unique.map((v) => `${v}`);
};

export const buildFilterConfigFromResponse = (
  filters: DynamicSupplyHardwareFiltersResponse | null
): FilterConfig => {
  const hasGpuFamily = !!filters?.gpu_family?.length;
  const hasGpuMemory = !!filters?.gpu_memory;
  const hasRegions = !!filters?.regions?.length;
  const hasCpuCores = !!filters?.cpu_cores;
  const hasDeviceMemory = !!filters?.device_memory;
  const hasStorage = !!filters?.storage;
  const hasSupplier = !!filters?.supplier;
  const hasConfidentialCompute = !!filters?.confidential_compute?.length;

  return {
    gpuFamily: {
      label: "GPU Family",
      options: hasGpuFamily ? filters!.gpu_family.map((g) => ({ label: g, value: g })) : [],
      disabled: !hasGpuFamily
    },
    gpuMemory: {
      label: "VRAM",
      options: hasGpuMemory
        ? buildSteppedRangeMB(filters!.gpu_memory.min, filters!.gpu_memory.max).map((v) => ({
            label: v,
            value: v
          }))
        : [],
      disabled: !hasGpuMemory
    },
    regions: {
      label: "Regions",
      options: hasRegions
        ? filters!.regions.map((v) => ({
            label: REGIONS_MAP[v as keyof typeof REGIONS_MAP],
            value: v
          }))
        : [],
      disabled: !hasRegions
    },
    cpuCores: {
      label: "CPU Cores",
      options: hasCpuCores
        ? buildSteppedRangeInt(filters!.cpu_cores.min, filters!.cpu_cores.max).map((v) => ({
            label: v,
            value: v
          }))
        : [],
      disabled: !hasCpuCores
    },
    deviceMemory: {
      label: "Device Memory",
      options: hasDeviceMemory
        ? buildSteppedRangeMB(filters!.device_memory.min, filters!.device_memory.max).map((v) => ({
            label: v,
            value: v
          }))
        : [],
      disabled: !hasDeviceMemory
    },
    storage: {
      label: "Storage",
      options: hasStorage
        ? buildSteppedRangeMB(filters!.storage.min, filters!.storage.max).map((v) => ({
            label: v,
            value: v
          }))
        : [],
      disabled: !hasStorage
    },
    supplier: {
      label: "Supplier",
      options: hasSupplier
        ? [
            { label: "IO", value: "internal" },
            { label: "Partner", value: "external" }
          ]
        : [],
      disabled: !hasSupplier
    },
    confidentialCompute: {
      label: "Confidential Compute",
      options: hasConfidentialCompute
        ? [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" }
          ]
        : [],
      disabled: !hasConfidentialCompute
    }
  };
};

type FilterDropdownProps = {
  filters: FilterValues;
  config: FilterConfig;
  onChangeValues: (key: FilterKey, values: string[]) => void;
  selectedApi?: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  availableApi?: DynamicSupplyHardwareFiltersResponse | null;
  onChangeSelected?: (next: Partial<DynamicSupplyHardwareFiltersResponse>) => void;
  hiddenFilterKeys?: FilterKey[];
  className?: string;
  itemClassName?: string;
};

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filters,
  config,
  onChangeValues,
  selectedApi,
  availableApi,
  onChangeSelected,
  hiddenFilterKeys = [],
  className,
  itemClassName
}) => {
  // API provides GB values; format to GB/TB smartly
  const formatCapacity = (gb: number) => {
    if (typeof gb !== "number" || isNaN(gb)) return "-";
    if (gb >= 1024) {
      const tb = gb / 1024;
      const value = tb % 1 === 0 ? tb.toFixed(0) : tb.toFixed(1);
      return `${parseFloat(value)} TB`;
    }
    return `${gb} GB`;
  };

  const setApiRange = (key: "gpu_memory" | "cpu_cores" | "device_memory" | "storage") => {
    return (range: { min: number; max: number }) => {
      if (!onChangeSelected) return;
      const next = { ...(selectedApi || {}) } as Partial<DynamicSupplyHardwareFiltersResponse>;
      (next as Record<string, unknown>)[key] = range;
      onChangeSelected(next);
    };
  };

  const oneStep = 1; // requested generic step

  return (
    <div className={cn("flex flex-row flex-wrap items-center gap-2", className)}>
      {(["regions", "gpuFamily", "supplier"] as FilterKey[])
        .filter((key) => !hiddenFilterKeys.includes(key))
        .map((key) => {
          const cfg = config[key];
          if (!cfg) return null;
          return (
            <MultiSelectInput
              key={key}
              placeholder={cfg.label}
              options={cfg.options}
              values={filters[key]}
              onChange={(vals) => onChangeValues(key, vals)}
              disabled={cfg.disabled}
              selectAllLabel={key === "gpuFamily" || key === "regions" ? "All" : undefined}
              className={cn("min-w-[120px]", itemClassName)}
            />
          );
        })}
      {!hiddenFilterKeys.includes("confidentialCompute") && (
        <SelectInput
          name="confidentialCompute"
          placeholder="Confidential Compute"
          value={filters.confidentialCompute?.[0] || ""}
          options={[
            { label: "Yes", value: "true" },
            { label: "No", value: "false" }
          ]}
          onChange={(value) => onChangeValues("confidentialCompute", [value])}
          disabled={config.confidentialCompute.disabled}
          triggerClassName="border-gray-light-1900 bg-gray-dark-600 text-gray-dark-2200"
          className="min-w-[225px] max-md:w-full"
          renderValue={(val) => {
            const label =
              val === "true"
                ? "Confidential Compute: Yes"
                : val === "false"
                  ? "Confidential Compute: No"
                  : "";
            return label ? <SelectValue key={val}>{label}</SelectValue> : null;
          }}
        />
      )}
      {/* GPU Memory */}
      {!hiddenFilterKeys.includes("gpuMemory") && (
        <RangeFilter
          label="VRAM"
          min={availableApi?.gpu_memory?.min ?? 0}
          max={availableApi?.gpu_memory?.max ?? 0}
          valueMin={(selectedApi?.gpu_memory as { min?: number } | undefined)?.min}
          valueMax={(selectedApi?.gpu_memory as { max?: number } | undefined)?.max}
          step={oneStep}
          disabled={!availableApi?.gpu_memory}
          formatValue={formatCapacity}
          onChange={setApiRange("gpu_memory")}
          className="min-w-[225px] max-md:w-full"
        />
      )}

      {/* CPU Cores */}
      {!hiddenFilterKeys.includes("cpuCores") && (
        <RangeFilter
          label="CPU Cores"
          min={availableApi?.cpu_cores?.min ?? 0}
          max={availableApi?.cpu_cores?.max ?? 0}
          valueMin={(selectedApi?.cpu_cores as { min?: number } | undefined)?.min}
          valueMax={(selectedApi?.cpu_cores as { max?: number } | undefined)?.max}
          step={oneStep}
          disabled={!availableApi?.cpu_cores}
          formatValue={(v) => `${v} cores`}
          onChange={setApiRange("cpu_cores")}
          className="min-w-[225px] max-md:w-full"
        />
      )}

      {/* Device Memory */}
      {!hiddenFilterKeys.includes("deviceMemory") && (
        <RangeFilter
          label="Device Memory"
          min={availableApi?.device_memory?.min ?? 0}
          max={availableApi?.device_memory?.max ?? 0}
          valueMin={(selectedApi?.device_memory as { min?: number } | undefined)?.min}
          valueMax={(selectedApi?.device_memory as { max?: number } | undefined)?.max}
          step={oneStep}
          disabled={!availableApi?.device_memory}
          formatValue={formatCapacity}
          onChange={setApiRange("device_memory")}
          className="min-w-[225px] max-md:w-full"
        />
      )}

      {/* Storage */}
      {!hiddenFilterKeys.includes("storage") && (
        <RangeFilter
          label="Storage"
          min={availableApi?.storage?.min ?? 0}
          max={availableApi?.storage?.max ?? 0}
          valueMin={(selectedApi?.storage as { min?: number } | undefined)?.min}
          valueMax={(selectedApi?.storage as { max?: number } | undefined)?.max}
          step={oneStep}
          disabled={!availableApi?.storage}
          formatValue={formatCapacity}
          onChange={setApiRange("storage")}
          className="min-w-[225px] max-md:w-full"
        />
      )}
    </div>
  );
};

export type LockedFilterPill = {
  id: string;
  label: string;
  /** Optional hover explanation (e.g. recipe-driven lock). */
  tooltip?: string;
};

type AppliedFiltersProps = {
  filters: FilterValues;
  config: FilterConfig;
  onClearKey: (key: FilterKey) => void;
  /** Shown like applied filters but not clearable (e.g. recipe VRAM requirement). */
  lockedFilters?: LockedFilterPill[];
};

const MAX_VISIBLE_PILLS = 4;
const pillClassName =
  "label-x-small flex items-center gap-[4px] rounded-[6px] border border-gray-dark-200 bg-gray-dark-600 p-1 px-2 leading-4 text-foreground";

export const AppliedFilters: React.FC<AppliedFiltersProps> = ({
  filters,
  config,
  onClearKey,
  lockedFilters = []
}) => {
  const [expanded, setExpanded] = useState(false);

  const groupedPills = useMemo(() => {
    return (Object.keys(config) as FilterKey[])
      .map((key) => ({ key, values: filters[key] }))
      .filter((g) => g.values && g.values.length > 0)
      .map(({ key, values }) => {
        const optionMap = new Map(config[key].options.map((o) => [o.value, o.label]));
        const displayValues = values.map((v) => optionMap.get(v) ?? v);
        const label = `${config[key].label}: ${displayValues.join(", ")}`;
        return { key, label };
      });
  }, [filters, config]);

  const clearableVisible = expanded ? groupedPills : groupedPills.slice(0, MAX_VISIBLE_PILLS);
  const hiddenCount = Math.max(0, groupedPills.length - clearableVisible.length);

  if (groupedPills.length === 0 && lockedFilters.length === 0) return null;

  return (
    <div className="flex flex-row flex-wrap gap-2 max-lg:w-full">
      {lockedFilters.map((pill) => {
        const pillEl = <span className={pillClassName}>{pill.label}</span>;
        if (!pill.tooltip) {
          return (
            <span key={pill.id} className={pillClassName}>
              {pill.label}
            </span>
          );
        }
        return (
          <SimpleTooltip
            key={pill.id}
            id={`locked-filter-${pill.id}`}
            content={pill.tooltip}
            usePortal
          >
            {pillEl}
          </SimpleTooltip>
        );
      })}
      {clearableVisible.map((pill) => (
        <button
          key={`${pill.key}`}
          type="button"
          className={pillClassName}
          onClick={() => onClearKey(pill.key)}
          title="Clear filter"
        >
          {pill.label}
          <XIcon className="h-4 w-4" />
        </button>
      ))}
      {hiddenCount > 0 && !expanded && (
        <button
          type="button"
          className="label-x-small rounded-[6px] border border-gray-dark-200 bg-gray-dark-600 p-1 px-2 leading-4 text-foreground"
          onClick={() => setExpanded(true)}
          title="Show more"
        >
          +{hiddenCount}
        </button>
      )}
      {expanded && groupedPills.length > MAX_VISIBLE_PILLS && (
        <button
          type="button"
          className="label-x-small rounded-[6px] border border-gray-dark-200 bg-gray-dark-600 p-1 px-2 leading-4 text-foreground"
          onClick={() => setExpanded(false)}
          title="Show less"
        >
          Show less
        </button>
      )}
    </div>
  );
};
